/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/no-extraneous-dependencies */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { client } from "./api-client";

export const useCreateInterBranchOrder = () =>
  useMutation({
    mutationFn: (data: any) =>
      client(`interbranch/services/create/request`, {
        data,
        successMessage: "Request submitted successfully!"
      })
  });

export const useGetInterBranchOrders = (data: any) =>
  useQuery({
    queryKey: [
      "GetInterBranchOrders",
      data?.pageNumber,
      data?.searchCriteria,
      data?.operationDate,
      data?.cashCenterId,
      data?.customerId,
      data?.onwardSPId,
      data?.sourceSPId,
      data?.pendingReason
    ],
    queryFn: ({ signal }) =>
      client(`interbranch/services/open/requests`, { data, signal })
  });

export const useCreateInterBranchBulkOrders = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) =>
      client(`interbranch/services/create/bulk/requests`, {
        data,
        successMessage: "Order(s) created successfully!"
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["GetInterBranchOrders"]
      });
    }
  });
};

export const useCancelInterBranchOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) =>
      client(`interbranch/services/cancel/services`, {
        data,
        successMessage: "Selected job(s) cancelled successfully!"
      }),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ["GetInterBranchOrders"] });
    }
  });
};

export const useAssignInterBranchRoute = () => {
  const queryClient = useQueryClient();
  return useMutation<any>({
    mutationFn: (data: any) =>
      client(`interbranch/services/assign/route`, {
        data,
        successMessage: "Route assigned successfully for selected job(s)!"
      }),
    onSuccess: () => {
      queryClient.resetQueries({
        queryKey: ["GetInterBranchOrders"]
      });
    }
  });
};

export const useGetInterBranchServiceDetails = (data: any) =>
  useQuery<any>({
    queryKey: ["GetInterBranchDetails", data?.requestId],
    queryFn: () =>
      client(`interbranch/services/request/details/${data?.requestId}`),
    enabled: Boolean(data?.requestId)
  });

export const useUpdateInterBranchServiceStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) =>
      client(`interbranch/services/update/request/status`, {
        data,
        successMessage: "Request status updated successfully!"
      }),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ["GetInterBranchDetails"] });
    }
  });
};

export const useEditInterBranchServiceJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) =>
      client(`interbranch/services/edit/request`, {
        data,
        successMessage: "Request edited successfully!"
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["GetInterBranchDetails"] });
    }
  });
};

export const useAssignBagSeals = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) =>
      client(`interbranch/services/bulk/bags/assignment`, {
        data,
        successMessage: "Bag Seal Assigned successfully!"
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["GetInterBranchOrders"] });
    }
  });
};

export const useGetSingCurrencyTemplate = () =>
  useMutation({
    mutationFn: (data: any) =>
      client(`interbranch/services/download/single/currency/template`, {
        data
      })
  });

export const useGetMultipleCurrencyTemplate = () =>
  useMutation({
    mutationFn: (data: any) =>
      client(`interbranch/services/download/multiple/currency/template`, {
        data
      })
  });

export const useGetCoinsTemplate = () =>
  useMutation({
    mutationFn: (data: any) =>
      client(`interbranch/services/download/coins/template`, { data })
  });

export const useAssignInterBranchBags = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) =>
      client(`interbranch/services/assign/consignments`, {
        data,
        successMessage: "Consignments added successfully!"
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["GetInterBranchDetails"] });
    }
  });
};

export const useUpdateInterBranchBags = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) =>
      client(`interbranch/services/update/consignments`, {
        data,
        successMessage: "Consignments updated successfully!"
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["GetInterBranchDetails"] });
    }
  });
};

export const useGetInterBranchIncomingOrders = ({
  isFetchOnMount,
  ...data
}: any) =>
  useQuery<any>({
    queryKey: [
      "InterBranchIncomingOrders",
      data?.cashCenterId,
      data?.operationDate,
      data?.shift,
      data?.routeNo
    ],
    queryFn: () =>
      client(`field/operations/dedicated/incoming/boxes`, { data }),
    enabled:
      Boolean(data?.routeNo) &&
      Boolean(data?.cashCenterId) &&
      Boolean(data?.operationDate) &&
      Boolean(isFetchOnMount)
  });

export const useGetInterBranchOutgoingOrders = ({
  isFetchOnMount,
  ...data
}: any) =>
  useQuery<{
    sealedPackages: any[];
    coinsBoxes: any[];
    shift: string;
    routeNo: string;
    teamLeaderName: string;
    teamLeaderId: string;
    isReadyForDispatch: boolean;
  }>({
    queryKey: [
      "GetInterBranchOutgoingOrders",
      data?.cashCenterId,
      data?.routeNo,
      data?.shift,
      data?.operationDate
    ],
    queryFn: () =>
      client(`field/operations/dedicated/route/consignments`, { data }),
    enabled:
      Boolean(data?.routeNo) &&
      Boolean(data?.cashCenterId) &&
      Boolean(data?.operationDate) &&
      Boolean(isFetchOnMount)
  });

export const useConfirmDispatchInterBranchOrders = () =>
  useMutation<any, Error, any>({
    mutationFn: (data: any) =>
      client(`field/operations/validate/dedicated/consignments/discrepancy`, {
        data,
        successMessage: "Dispatch successfully!"
      })
  });

export const useRecordDispatchDiscrepancy = () =>
  useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(`field/operations/verify/dedicated/consignments`, { data })
  });

export const useConfirmReceiveReceipt = () =>
  useMutation({
    mutationFn: (data: any) =>
      client(`field/operations/dedicated/confirm/receipt`, { data })
  });

export const useRecordReceivedDiscrepancy = () =>
  useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(`field/operations/dedicated/record/discrepancy`, { data })
  });

export const useUpdateInterBranchAfterCompletion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) =>
      client(`interbranch/services/update/evhr/request`, {
        data,
        successMessage: "Request updated successfully!"
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["GetInterBranchDetails"] });
    }
  });
};

export const useDownloadInterbranchReceivingReport = () =>
  useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(`field/operations/dedicated/received/consignments`, { data })
  });

export const useDownloadInterBranchDispatchDiscrepancy = () =>
  useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(`field/operations/download/dedicated/discrepancy`, { data })
  });

export const useValidateBulkBagsAssignment = () =>
  useMutation({
    mutationFn: (data: any) =>
      client(`interbranch/services/validate/bulk/bags/assignment`, {
        data,
        errorMessage: "Invalid barcodes found."
      })
  });
